// Route: /notes/[id] — TipTap editor. Lazy split (heavy bundle).
// URL state: ?demo=loading|empty|error|streaming|quota-exceeded|paywall
//
// РАЗВИЛКА U6 (спека §6.8б): конспект, сохранённый из ответа репетитора
// (`notes.source_message_id IS NOT NULL`), открывается ПРОСМОТРОМ, а редактор
// для него не монтируется вовсе. Решение принимается здесь, а не внутри
// `NotesEditor`: там оно было бы уже после `useEditor`, то есть автосейв,
// slash-меню и «Перегенерировать» всё равно поднялись бы — и разошлись бы с
// серверным гейтом read-only в write-handler'е `notes`.
//
// Признак читается из локальной SQLite (PowerSync) — он есть офлайн и не
// требует круга по сети до первого кадра.
//
// См. docs/ui-spec/notes.[id].{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, Skeleton } from "@studyhub/ui";
import { IconFileText } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { shouldMountNoteEditor, useNote } from "@/components/notes/_api";
import { useSavedAnswerNote } from "@/components/notes/saved-answer/use-saved-answer-note";
import { LazyRouteBoundary } from "@/components/shared/lazy-route-boundary";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import type { SyncedDetailPhase } from "@/hooks/resolve-synced-detail-phase";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useTrackView } from "@/hooks/use-track-view";
import { fromSearchSchema } from "@/lib/back-from";

const NotesEditor = lazy(() => import("@/components/notes/editor/notes-editor"));
const SavedAnswerView = lazy(() =>
	import("@/components/notes/saved-answer/saved-answer-view").then((m) => ({
		default: m.SavedAnswerView,
	})),
);

const searchSchema = z
	.object({
		demo: z
			.enum(["default", "loading", "empty", "error", "streaming", "quota-exceeded", "paywall"])
			.optional(),
		/**
		 * Режим правки. Отсутствует — ЧТЕНИЕ (умолчание): конспект чаще
		 * перечитывают, чем правят. Литерал `1`, как в URL-контракте воркспейса
		 * курса (`courses/workspace/_search.ts`), чтобы режим правки читался
		 * одинаково на обеих поверхностях конспекта.
		 */
		edit: z.literal(1).optional(),
	})
	.merge(fromSearchSchema);

export const Route = createFileRoute("/notes/$noteId")({
	validateSearch: tolerantSearch(searchSchema),
	component: NoteRoute,
});

function NoteRoute(): React.ReactElement {
	const { noteId } = useParams({ from: "/notes/$noteId" });
	const { demo, from, edit } = useSearch({ from: "/notes/$noteId" });
	const navigate = useNavigate({ from: "/notes/$noteId" });
	// Пустой конспект открывается сразу правкой: читать в нём нечего.
	const editing = edit === 1 || noteId === "new";
	// В demo-режиме (?demo=...) noteId — placeholder/чужой → recordView бросил бы
	// NOT_FOUND. null → хук no-op'ит (см. use-track-view), без сетевого шума.
	useTrackView("note", demo ? null : noteId);
	useArtifactWatching(demo ? null : markArtifactSeenKey("notes", noteId));

	// В demo-режиме витрины развилка не нужна: там заведомо обычный редактор.
	const saved = useSavedAnswerNote(demo ? "" : noteId);
	const noteQuery = useNote(demo || noteId === "new" ? undefined : noteId);

	// Пока локальная строка не прочитана — скелетон, а не редактор: смонтировать
	// редактор и через миг подменить его просмотром означало бы создать и тут же
	// выбросить инстанс TipTap (мигание и лишний autosave-таймер).
	if (saved.isLoading) return <EditorRouteSkeleton />;

	// Строку читает роут и отдаёт её просмотру готовой: второй наблюдатель за той
	// же строкой означал бы лишний ре-рендер на каждое изменение локальной БД.
	if (saved.note) {
		return (
			<LazyRouteBoundary>
				<Suspense fallback={<EditorRouteSkeleton />}>
					<SavedAnswerView note={saved.note} from={from} />
				</Suspense>
			</LazyRouteBoundary>
		);
	}

	// Редактор (и его автосейв) не монтируем, пока шов PowerSync↔tRPC не ready
	// и тело конспекта не известно (ready + пустой content + remote pending).
	if (
		!demo &&
		noteId !== "new" &&
		!shouldMountNoteEditor({
			phase: noteQuery.phase,
			content: noteQuery.data?.content,
			remoteStatus: noteQuery.remoteStatus,
			generationStatus: noteQuery.data?.generationStatus,
		})
	) {
		return (
			<NoteRoutePhase
				phase={noteQuery.phase === "ready" ? "loading" : noteQuery.phase}
				onRetry={noteQuery.refetch}
				onBack={() => void navigate({ to: "/notes" })}
			/>
		);
	}

	return (
		<LazyRouteBoundary>
			<Suspense fallback={<EditorRouteSkeleton />}>
				<NotesEditor
					noteId={noteId}
					demo={demo}
					from={from}
					editing={editing}
					onEditingChange={(next) =>
						void navigate({
							search: (prev) => ({ ...prev, edit: next ? 1 : undefined }),
							replace: true,
						})
					}
				/>
			</Suspense>
		</LazyRouteBoundary>
	);
}

function NoteRoutePhase({
	phase,
	onRetry,
	onBack,
}: {
	phase: SyncedDetailPhase;
	onRetry: () => void;
	onBack: () => void;
}): React.ReactElement {
	const { t } = useTranslation("notes");
	const { t: tCommon } = useTranslation("common");
	if (phase === "sync_pending") {
		return (
			<div className="flex min-h-[calc(100dvh-4rem)] flex-col items-center justify-center px-4">
				<OfflineDetailState onRetry={onRetry} />
			</div>
		);
	}
	if (phase === "not_found") {
		return (
			<div className="flex min-h-[calc(100dvh-4rem)] flex-col items-center justify-center px-4">
				<EmptyState
					icon={<IconFileText stroke={1.5} />}
					title={t("editor.errorTitle")}
					description={t("editor.errorHint")}
					action={
						<Button variant="outline" onClick={onBack} className="cursor-pointer">
							{t("editor.backToList")}
						</Button>
					}
				/>
			</div>
		);
	}
	if (phase === "load_failed") {
		return (
			<div className="flex min-h-[calc(100dvh-4rem)] flex-col items-center justify-center px-4">
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorHint")}
					action={
						<Button onClick={onRetry} className="cursor-pointer">
							{tCommon("actions.retry")}
						</Button>
					}
				/>
			</div>
		);
	}
	return <EditorRouteSkeleton />;
}

function EditorRouteSkeleton(): React.ReactElement {
	return (
		<div className="flex flex-col min-h-[calc(100dvh-4rem-var(--audio-player-height,0px))] bg-background">
			<div className="h-12 border-b border-border bg-card flex items-center gap-2 px-4">
				<Skeleton className="h-8 w-32 rounded-[var(--radius-tile)]" />
				<Skeleton className="h-8 w-28 rounded-[var(--radius-tile)]" />
			</div>
			<div className="flex-1 px-3 py-5 lg:px-6 lg:py-8">
				<div className="mx-auto max-w-reading rounded-[var(--radius-card)] border border-border bg-card shadow-e1 px-4 lg:px-6 py-6 space-y-4">
					<Skeleton className="h-9 w-2/3" />
					<Skeleton className="h-5 w-1/3" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-5/6" />
					<Skeleton className="h-4 w-11/12" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-28 w-full rounded-[var(--radius-md)]" />
				</div>
			</div>
		</div>
	);
}
